"use client";

import { useMemo, useState } from "react";
import SolarIcon from "@/components/ui/SolarIcon/SolarIcon";
import {
    Avancer,
    ChampDate,
    Curseur,
    Ecran,
    Liste,
    Question,
    Recommencer,
    Resultat,
    Segments,
    euros,
    remplir,
    useMontants,
    useParcours,
} from "./Journey";
import {
    statutTva,
    statutTvaMixte,
    caAnnualise,
    seuilsTvaAjustes,
    SEUILS_TVA,
    PLAFONDS,
} from "@/lib/tools/microEnterprise";
import { joursAvant } from "@/lib/tools/electronicInvoicing";
import { mentionFranchise } from "@/lib/tools/vatMentions";
import { reprendre } from "@/lib/tools/dossier";
import { useDossierOutil, useSignalerDossier } from "./ToolsDossier";

/**
 * Franchise en base de TVA : où j'en suis, et surtout à partir de QUAND.
 *
 * Les simulateurs existants répondent par oui ou par non. La question utile est
 * la date, parce que les deux seuils n'ont pas du tout le même effet :
 *
 *   - le seuil MAJORÉ rend redevable LE JOUR MÊME du franchissement, au milieu
 *     d'une année pendant laquelle on a facturé sans TVA ;
 *   - le seuil de BASE ne rend redevable qu'au 1er janvier suivant.
 *
 * C'est le premier qui coûte cher : la TVA est due sur les opérations réalisées
 * à compter de ce jour, et une facture émise sans TVA la veille reste valable
 * quand celle du lendemain ne l'est plus.
 *
 * Le seuil unique à 25 000 € qu'on lit encore partout a été voté, reporté,
 * suspendu, puis abandonné par la loi du 3 novembre 2025. Il n'est jamais entré
 * en application.
 *
 * ON NE DEMANDE QUE LES CHIFFRES QUI EXISTENT. La franchise se juge sur deux
 * chiffres, l'année dernière et l'année en cours, mais la plupart des lecteurs
 * n'ont pas encore d'année dernière : la question « où en êtes-vous ? » décide
 * des curseurs qu'on montre. Celui qui prépare son projet arrive au résultat
 * avec un curseur à zéro pour explorer, celui qui a démarré cette année ne
 * répond que pour l'année en cours, et seul celui qui a une année complète
 * derrière lui donne les deux chiffres.
 *
 * ET LA QUESTION DE L'ANNÉE EN COURS NE SE POSE PAS TOUJOURS. Quand le chiffre
 * d'affaires de l'année dernière dépasse déjà le seuil de base, on est
 * redevable depuis le 1er janvier quoi qu'il arrive cette année : demander le
 * chiffre de l'année en cours ne changerait ni le verdict ni sa date, et
 * laisser un curseur le faire varier afficherait un « redevable depuis le jour
 * du franchissement » faux, puisque la TVA est due depuis janvier. Le parcours
 * saute donc directement au résultat.
 *
 * L'ANNÉE DE CRÉATION A SES PROPRES SEUILS. Celui qui a démarré cette année, ou
 * qui prépare son projet, donne sa date de début : les seuils se réduisent à
 * proportion des jours d'activité jusqu'au 31 décembre (BOFiP § 285 à 295).
 * Lancé le 12 juin, un prestataire doit la TVA au-delà de 22 942 €, et non de
 * 41 250 € : le parcours le jugeait sur les seuils pleins. Les activités
 * saisonnières ou intermittentes les gardent, d'où la question sous la date.
 *
 * LES SOINS N'ONT PAS DE SEUIL. Un psychologue ou un ostéopathe choisissait
 * « Prestations de services », entrait tout son chiffre d'affaires, et le
 * parcours lui annonçait une TVA qu'il ne doit pas : ses soins sont exonérés
 * en eux-mêmes (CGI, art. 261, 4, 1°) et n'entrent même pas dans le chiffre
 * comparé aux seuils (BOFiP BOI-TVA-DECLA-40-10-10 § 230). La carte « Un
 * métier de soin » le lui dit, avec ce qui reste taxable, puis ne situe face
 * aux seuils que ses autres prestations (reference-2026.json,
 * tva_exoneration_soins et tva_soins_hors_chiffre_franchise).
 *
 * LES COURS PARTICULIERS NON PLUS, à trois conditions : les donner soi-même,
 * être payé directement par ses élèves, travailler sans salarié (CGI, art.
 * 261, 4, 4°, b ; BOFiP BOI-TVA-CHAMP-30-10-20-50 § 360 à 370 ; F32231). La
 * carte « Des cours particuliers » suit le même chemin que celle des soins
 * (tva_exoneration_cours).
 */

/* Le parcours, écran par écran. Nommés plutôt que numérotés : il se ramifie. */
const PHASES = { activite: 0, soins: 0, cours: 0, situation: 0, debutEnCours: 1, anneeN1: 1, dateN1: 1, caN1: 1, caEnCours: 1, resultat: 2 };

const L = {
    fr: {
        phases: ["Votre activité", "Vos chiffres", "Votre TVA"],
        retour: "Retour",
        continuer: "Continuer",
        recommencer: "Refaire la simulation",

        qActivite: "Quelle est votre activité ?",
        qActiviteAide:
            "Les seuils de la franchise, c'est-à-dire du droit de facturer sans TVA, dépendent de ce que vous vendez : {services} pour les prestations de services, {reglementees} pour quelques professions à régime particulier, et {global} de chiffre d'affaires total pour tout le monde, prestataires compris. Les soins et les cours particuliers payés par vos élèves, eux, sont exonérés de TVA et n'ont aucun seuil.",
        global: "Vente, hébergement, restauration",
        globalDetail: "Achat-revente, chambres d'hôtes, vente à consommer sur place",
        services: "Prestations de services",
        servicesDetail:
            "Conseil, création, bâtiment, coaching, formation, et toute activité libérale sauf celles des trois cartes suivantes. Activité mixte ? Choisissez cette carte et n'entrez que votre part de prestations",
        reglementees: "Avocats, auteurs, artistes-interprètes",
        reglementeesDetail: "Pour leurs seules opérations réglementées et cessions de droits",
        soins: "Un métier de soin",
        soinsDetail: "Psychologue, psychothérapeute, ostéopathe, chiropracteur, diététicien, profession paramédicale",

        soinsTitre: "Vos soins sont exonérés de TVA, quel que soit votre chiffre d'affaires",
        soinsAide:
            "Pour vos soins, vous n'avez aucun seuil de TVA à surveiller, ni {base}, ni {majore} : ils ne comptent même pas dans le chiffre d'affaires comparé aux seuils.",
        soinsPoints: [
            "Seuls vos actes de diagnostic ou de soin sont exonérés.",
            "Le reste suit les règles de TVA de n'importe quel prestataire : une expertise, une activité de conseil, la location de votre cabinet à un confrère, la vente d'un produit à quelqu'un que vous ne soignez pas.",
            "Si vous êtes psychologue ou psychothérapeute, seuls vos actes réalisés depuis que vous avez le droit de porter le titre sont exonérés.",
            "Vous êtes sophrologue, naturothérapeute, étiopathe, ou acupuncteur sans diplôme de médecin ? Ces métiers ne sont pas exonérés : revenez en arrière et choisissez « Prestations de services ».",
        ],
        soinsQuestion: "Vous avez aussi des prestations qui ne sont pas des soins ?",
        exonereContinuer: "Situer ces prestations face aux seuils",
        soinsRappel:
            "Ce résultat ne concerne que vos prestations qui ne sont pas des soins. Vos soins, eux, restent exonérés de TVA, quel que soit ce chiffre.",
        caAideHorsSoins: "Comptez seulement vos prestations qui ne sont pas des soins, hors taxes. Laissez vos soins de côté.",

        cours: "Des cours particuliers",
        coursDetail: "Soutien scolaire, langues, musique, danse, sport, payés directement par vos élèves",
        coursTitre: "Vos cours sont exonérés de TVA, à trois conditions",
        coursAide:
            "Cela vaut pour tout enseignement scolaire, universitaire, professionnel, artistique ou sportif : mathématiques, langues, piano, chant, danse, judo, natation, tennis, ski. Si vous remplissez les trois conditions ci-dessous, vous n'avez aucun seuil de TVA à surveiller pour vos cours, ni {base}, ni {majore} : ils ne comptent même pas dans le chiffre d'affaires comparé aux seuils.",
        coursPoints: [
            "Vous donnez vos cours vous-même, en indépendant, et non dans une école que vous dirigez.",
            "Vos élèves vous paient directement. Si c'est l'entreprise de vos stagiaires qui paie la formation, cette condition n'est pas remplie : vous faites alors de la formation professionnelle continue, exonérée seulement avec une attestation de l'administration.",
            "Vous travaillez sans salarié. Si vous embauchez un assistant, vous perdez l'exonération ; un local aménagé pour vos cours, en revanche, ne change rien.",
        ],
        coursQuestion: "Vous avez aussi des prestations qui ne sont pas des cours exonérés ?",
        coursRappel:
            "Ce résultat ne concerne que vos prestations qui ne sont pas des cours exonérés. Les cours que vos élèves vous paient directement, eux, restent exonérés de TVA, quel que soit ce chiffre.",
        caAideHorsCours: "Comptez seulement vos prestations qui ne sont pas des cours exonérés, hors taxes. Laissez ces cours de côté.",

        qSituation: "Où en êtes-vous ?",
        qSituationAide:
            "La franchise se juge sur deux chiffres, celui de l'année dernière et celui de l'année en cours. Selon votre situation, l'un des deux n'existe pas encore, et nous ne vous le demandons pas.",
        projet: "Je prépare mon projet",
        projetDetail: "Rien d'encaissé pour l'instant : vous explorerez les seuils sur le résultat",
        creee: "J'ai démarré cette année",
        creeeDetail: "Pas d'année complète derrière vous",
        etablie: "J'ai au moins une année derrière moi",
        etablieDetail: "Vous avez un chiffre d'affaires pour l'année dernière",

        qAnneeN1: "L'an dernier, étiez-vous installé toute l'année ?",
        qAnneeN1Aide:
            "Si vous avez démarré en cours d'année, le chiffre d'affaires de cette première année est d'abord ramené à l'année pleine, puis comparé au seuil. Par exemple, 37 000 € encaissés à partir du 1er octobre restent sous les 37 500 €, mais valent 146 793 € une fois annualisés, et la TVA est due dès le 1er janvier.",
        anneeEntiere: "Oui, du 1er janvier au 31 décembre",
        anneeEntiereDetail: "Votre chiffre d'affaires se compare tel quel au seuil",
        anneePartielle: "Non, j'ai commencé en cours d'année",
        anneePartielleDetail: "Votre chiffre d'affaires sera ramené à l'année pleine",
        anneeSaisonniere: "Mon activité est saisonnière ou intermittente",
        anneeSaisonniereDetail: "Ces activités échappent à l'annualisation",

        qDateN1: "Quel jour avez-vous commencé ?",
        qDateN1Aide:
            "Le calcul de l'administration est celui-ci : chiffre d'affaires × 365 ÷ jours d'activité. Plus vous avez commencé tard, plus le chiffre annualisé monte.",
        dateN1HorsAnnee: "Choisissez une date de {annee}, l'année qui précède celle en cours.",
        dateN1Effet: "{jours} jours d'activité : {ca} encaissés vaudraient {annualise} sur une année pleine.",

        qDebutCreee: "Quel jour avez-vous commencé ?",
        qDebutProjet: "Quel jour comptez-vous démarrer ?",
        qDebutAide:
            "L'année où l'activité démarre, les seuils de la franchise se réduisent à proportion des jours qui restent jusqu'au 31 décembre. Plus le démarrage est tardif, plus la TVA arrive tôt : lancé le 12 juin, un prestataire la doit au-delà de 22 942 €, et non de 41 250 €.",
        debutEffet: "{jours} jours d'activité la première année : vos seuils passent de {basePleine} et {majorePleine} à {base} et {majore}.",
        debutEffetMixte:
            "{jours} jours d'activité la première année : sur votre total, vos seuils passent à {base} et {majore} ; sur vos prestations, à {baseServices} et {majoreServices}.",
        debutPlein: "Une année entière : vos seuils restent pleins.",
        debutVide: "Indiquez la date : c'est elle qui fixe vos seuils de la première année.",
        debutHorsCreee: "Choisissez une date de {annee}, jusqu'à aujourd'hui.",
        debutHorsProjet: "Choisissez une date à venir, au plus tard le 31 décembre {annee}.",
        debutSaisonnier: "Votre activité est-elle saisonnière ou intermittente ?",
        debutSaisonnierEffet: "Une activité saisonnière ou intermittente garde les seuils pleins, quelle que soit sa date de début.",
        saisonnierNon: "Non",
        saisonnierOui: "Oui",
        prorataTexte:
            "Vous avez démarré le {date} : {jours} jours d'activité cette année. Vos seuils sont réduits d'autant, {base} et {majore} au lieu de {basePleine} et {majorePleine}.",
        prorataTexteProjet:
            "Vous démarrez le {date} : {jours} jours d'activité la première année. Vos seuils sont réduits d'autant, {base} et {majore} au lieu de {basePleine} et {majorePleine}.",
        qCaEnCoursCreee: "Et depuis votre démarrage, combien avez-vous encaissé ?",
        qCaEnCoursCreeeAide:
            "C'est ce chiffre qui décide de la date. Il se compare à vos seuils réduits : au-delà du seuil majoré, vous facturez la TVA dès le jour du dépassement, sans attendre janvier.",

        mixte: "Les deux, vente et prestations",
        mixteDetail: "Deux conditions à tenir en même temps, et aucune ne dispense de l'autre",
        partServices: "Dont prestations de services",
        partServicesAide:
            "La part de ce total qui vient de prestations. Le reste est de la vente.",
        conditionTotal:
            "C'est votre TOTAL qui a franchi le seuil : {total} pour un seuil de base de {base}, alors que vos prestations seules restaient dessous.",
        conditionPrestations:
            "Ce sont vos PRESTATIONS seules qui ont franchi leur sous-plafond : {presta} pour un seuil de base de {base}, alors que votre total restait dessous.",

        qCaN1: "Quel chiffre d'affaires avez-vous encaissé l'année dernière ?",
        qCaN1Aide:
            "Le seuil de base s'apprécie sur l'année précédente : si vous l'avez dépassé l'an dernier, vous devez facturer la TVA depuis le 1er janvier, que vous l'ayez fait ou non.",
        caN1: "Chiffre d'affaires de l'année dernière",

        qCaEnCours: "Et depuis le 1er janvier, combien avez-vous encaissé ?",
        qCaEnCoursAide:
            "C'est ce chiffre qui décide de la date : le seuil majoré se franchit en cours d'année et vous rend redevable le jour même, c'est-à-dire que vous facturez la TVA dès ce jour, sans attendre janvier.",
        caEnCours: "Chiffre d'affaires encaissé cette année",
        caVise: "Chiffre d'affaires que vous visez pour votre première année",
        caAide: "Votre chiffre d'affaires hors taxes, toutes vos ventes et prestations de l'année confondues.",
        explorerTitre: "Explorez votre année en cours",
        explorerTitreProjet: "Explorez votre première année",
        caExplorer: "Déplacez le curseur : la jauge et le verdict se recalculent.",

        resultatTitre: "Votre situation",

        franchiseTitre: "Vous êtes en franchise de TVA",
        franchiseTexte:
            "Vous facturez sans TVA, avec une mention d'exonération sur chaque facture, le plus souvent « {mention} », et vous ne pouvez déduire aucune TVA sur vos achats.",
        margeBase: "Il vous reste {marge} avant le seuil de base",
        margeMajore: "et {marge} avant le seuil majoré",
        margeMajoreSeule: "Il vous reste {marge} avant le seuil majoré",
        auSeuilBase: "Vous êtes exactement au seuil de base : un euro de plus le franchit.",
        margeSuite:
            "Le seuil de base vous ferait facturer la TVA à partir du 1er janvier {annee}, le seuil majoré dès le jour où vous le franchissez.",

        bascule1erJanvierTitre: "Vous deviendrez redevable le 1er janvier {annee}",
        bascule1erJanvierTexte:
            "Vous avez dépassé le seuil de base de {depassement}, mais pas le seuil majoré. La franchise court donc jusqu'au 31 décembre, et vous facturez la TVA à partir du 1er janvier {annee}, {echeance}.",
        echeanceJours: "dans {jours} jours",
        echeanceDemain: "demain",
        margeMajoreBascule:
            "Il vous reste {marge} avant le seuil majoré, qui, lui, bascule le jour même.",
        auSeuilMajore:
            "Vous êtes exactement au seuil majoré : un euro de plus vous rend redevable le jour même.",

        depuisJanvierTitre: "Vous êtes redevable depuis le 1er janvier {annee}",
        depuisJanvierTexte:
            "Votre chiffre d'affaires de l'année dernière, {ca}, a dépassé le seuil de base de {depassement}. Toutes vos factures de cette année doivent porter la TVA, et vous pouvez déduire celle de vos achats. Si vous avez facturé sans TVA depuis janvier, ces factures sont à rectifier, acomptes compris. Nous ne vous avons pas demandé ce que vous encaissez cette année : cela ne change plus rien à cette date.",

        immediatTitre: "Vous êtes redevable depuis le jour du franchissement",
        immediatTexte:
            "Vous avez dépassé le seuil majoré de {depassement}. La TVA est due sur les opérations réalisées à compter de ce jour, sans attendre le 1er janvier. Les factures émises avant restent valables telles quelles ; celles émises depuis doivent être rectifiées si elles ne portent pas la TVA.",

        jaugeTitre: "Votre année en cours face aux deux seuils",
        jaugeTitreProjet: "Votre première année face aux deux seuils",
        jaugeTitreN1: "Votre année dernière face aux deux seuils",
        seuilBase: "Seuil de base",
        seuilMajore: "Seuil majoré",

        rappelTitre: "Ces seuils n'ont rien à voir avec ceux du régime micro",
        rappelTexte:
            "Dépasser un seuil de TVA ne vous fait pas sortir de la micro-entreprise, dont le plafond est bien plus haut : {services} en prestations de services, {vente} en vente. Vous devenez simplement redevable de la TVA, et vous restez micro-entrepreneur.",
        pieges: [
            "Toute TVA mentionnée sur une facture est due du seul fait de la facturation, même en franchise, et votre client ne peut pas la récupérer : il la perd.",
            "En franchise, aucune TVA n'est déductible sur vos achats : c'est la contrepartie.",
            "Pour les avocats, auteurs et artistes-interprètes, ces seuils ne valent que sur les opérations réglementées et les cessions de droits. Le reste de leur activité relève d'une franchise à part : {base} de base, {majore} majoré.",
            "Les soins des professions de santé réglementées, psychologues et ostéopathes compris, sont exonérés de TVA et ne comptent pas dans les seuils : seules leurs autres prestations s'y comparent.",
            "Les cours particuliers que vous donnez vous-même, sans salarié, et que vos élèves vous paient directement sont exonérés de TVA et ne comptent pas dans les seuils.",
        ],
    },
    en: {
        phases: ["Your activity", "Your figures", "Your VAT"],
        retour: "Back",
        continuer: "Continue",
        recommencer: "Start over",

        qActivite: "What is your activity?",
        qActiviteAide:
            "The exemption thresholds, that is the right to invoice without VAT, depend on what you sell: {services} for services, {reglementees} for a few professions with their own scheme, and {global} of total turnover for everyone, service providers included. Care, and private lessons paid for by your pupils, are exempt from VAT and have no threshold at all.",
        global: "Goods, accommodation, catering",
        globalDetail: "Retail, guest rooms, food served on the premises",
        services: "Services",
        servicesDetail:
            "Consulting, creative work, building, coaching, training, and any liberal profession other than those on the next three cards. Mixed activity? Pick this card and enter your services share only",
        reglementees: "Lawyers, authors, performing artists",
        reglementeesDetail: "For their regulated work and transfers of rights (cessions de droits) only",
        soins: "A care profession",
        soinsDetail: "Psychologist, psychotherapist, osteopath, chiropractor, dietitian, regulated paramedical profession",

        soinsTitre: "Your care is exempt from VAT, whatever your turnover",
        soinsAide:
            "For your care, you have no VAT threshold to watch, neither {base} nor {majore}: it does not even count in the turnover compared with the thresholds.",
        soinsPoints: [
            "Only your acts of diagnosis or care are exempt.",
            "Everything else follows the VAT rules of any service provider: an expert report, consulting work, renting your practice to a colleague, selling a product to someone you do not treat.",
            "If you are a psychologist or psychotherapist, only the acts you carried out since you became entitled to use the title are exempt.",
            "Are you a sophrologist, naturotherapist, etiopath, or an acupuncturist without a medical degree? These professions are not exempt: go back and pick « Services ».",
        ],
        soinsQuestion: "Do you also provide services that are not care?",
        exonereContinuer: "Place those services against the thresholds",
        soinsRappel:
            "This result only covers your services that are not care. Your care itself stays exempt from VAT, whatever this figure.",
        caAideHorsSoins: "Count only your services that are not care, excluding VAT. Leave your care aside.",

        cours: "Private lessons",
        coursDetail: "Tutoring, languages, music, dance, sport, paid for directly by your pupils",
        coursTitre: "Your lessons are exempt from VAT, on three conditions",
        coursAide:
            "This applies to any school, university, vocational, artistic or sports teaching: maths, languages, piano, singing, dance, judo, swimming, tennis, skiing. If you meet the three conditions below, you have no VAT threshold to watch for your lessons, neither {base} nor {majore}: they do not even count in the turnover compared with the thresholds.",
        coursPoints: [
            "You give your lessons yourself, as an independent, and not within a school you run.",
            "Your pupils pay you directly. If your trainees' employer pays for the training, this condition is not met: you are then providing continuing vocational training, exempt only with a certificate from the authorities.",
            "You work without employees. If you hire an assistant, you lose the exemption; premises fitted out for your lessons, on the other hand, change nothing.",
        ],
        coursQuestion: "Do you also provide services that are not exempt lessons?",
        coursRappel:
            "This result only covers your services that are not exempt lessons. Your lessons paid for directly by your pupils stay exempt from VAT, whatever this figure.",
        caAideHorsCours: "Count only your services that are not exempt lessons, excluding VAT. Leave those lessons aside.",

        qSituation: "Where do you stand?",
        qSituationAide:
            "The exemption is judged on two figures, last year's and this year's. Depending on your situation, one of them does not exist yet, and we will not ask for it.",
        projet: "I am planning my project",
        projetDetail: "Nothing taken in yet: you will explore the thresholds on the result",
        creee: "I started this year",
        creeeDetail: "No full year behind you yet",
        etablie: "I have at least one year behind me",
        etablieDetail: "You have a turnover figure for last year",

        qAnneeN1: "Last year, were you in business the whole year?",
        qAnneeN1Aide:
            "If you started during the year, the turnover of that first year is first scaled up to a full year, then compared to the threshold. For example, 37,000 € collected from 1 October stays under 37,500 €, but is worth 146,793 € once annualised, and VAT is due from 1 January.",
        anneeEntiere: "Yes, from 1 January to 31 December",
        anneeEntiereDetail: "Your turnover is compared to the threshold as it stands",
        anneePartielle: "No, I started during the year",
        anneePartielleDetail: "Your turnover will be scaled up to a full year",
        anneeSaisonniere: "My activity is seasonal or intermittent",
        anneeSaisonniereDetail: "These activities escape the scaling-up",

        qDateN1: "What day did you start?",
        qDateN1Aide:
            "The tax authority's calculation is this one: turnover × 365 ÷ days in business. The later you started, the higher the annualised figure.",
        dateN1HorsAnnee: "Choose a date in {annee}, the year before the current one.",
        dateN1Effet: "{jours} days in business: {ca} collected would be worth {annualise} over a full year.",

        qDebutCreee: "What day did you start?",
        qDebutProjet: "What day do you plan to start?",
        qDebutAide:
            "In the year a business starts, the exemption thresholds shrink in proportion to the days left until 31 December. The later the start, the sooner VAT arrives: a service provider starting on 12 June owes it beyond 22,942 €, not 41,250 €.",
        debutEffet: "{jours} days in business in the first year: your thresholds go from {basePleine} and {majorePleine} to {base} and {majore}.",
        debutEffetMixte:
            "{jours} days in business in the first year: on your total, your thresholds go down to {base} and {majore}; on your services, to {baseServices} and {majoreServices}.",
        debutPlein: "A full year: your thresholds apply in full.",
        debutVide: "Enter the date: it sets your thresholds for the first year.",
        debutHorsCreee: "Choose a date in {annee}, up to today.",
        debutHorsProjet: "Choose a date to come, by 31 December {annee} at the latest.",
        debutSaisonnier: "Is your activity seasonal or intermittent?",
        debutSaisonnierEffet: "A seasonal or intermittent activity keeps the full thresholds, whatever its start date.",
        saisonnierNon: "No",
        saisonnierOui: "Yes",
        prorataTexte:
            "You started on {date}: {jours} days in business this year. Your thresholds shrink accordingly, to {base} and {majore} instead of {basePleine} and {majorePleine}.",
        prorataTexteProjet:
            "You start on {date}: {jours} days in business in your first year. Your thresholds shrink accordingly, to {base} and {majore} instead of {basePleine} and {majorePleine}.",
        qCaEnCoursCreee: "And since you started, how much have you collected?",
        qCaEnCoursCreeeAide:
            "This figure decides the date. It is compared with your reduced thresholds: beyond the higher one, you charge VAT from the day you cross it, without waiting for January.",

        mixte: "Both, goods and services",
        mixteDetail: "Two conditions to meet at once, and neither excuses the other",
        partServices: "Of which services",
        partServicesAide: "The part of that total that comes from services. The rest is goods.",
        conditionTotal:
            "It is your TOTAL that crossed the threshold: {total} against a basic threshold of {base}, while your services alone stayed below.",
        conditionPrestations:
            "It is your SERVICES alone that crossed their sub-threshold: {presta} against a basic threshold of {base}, while your total stayed below.",

        qCaN1: "How much turnover did you collect last year?",
        qCaN1Aide:
            "The basic threshold is assessed on the previous year: if you passed it last year, you owe VAT on everything since 1 January, whether you charged it or not.",
        caN1: "Last year's turnover",

        qCaEnCours: "And since 1 January, how much have you collected?",
        qCaEnCoursAide:
            "This figure decides the date: the higher threshold is crossed during the year and makes you liable on the day itself, meaning you charge VAT from that day on, without waiting for January.",
        caEnCours: "Turnover collected this year",
        caVise: "Turnover you are aiming for in your first year",
        caAide: "Your turnover excluding VAT, all your sales and services for the year together.",
        explorerTitre: "Explore your current year",
        explorerTitreProjet: "Explore your first year",
        caExplorer: "Move the slider: the gauge and the verdict recalculate.",

        resultatTitre: "Your situation",

        franchiseTitre: "You are under the VAT exemption",
        franchiseTexte:
            "You invoice without VAT, with an exemption line on each invoice, usually \"{mention}\", and you cannot reclaim any VAT on your purchases.",
        margeBase: "You have {marge} left before the basic threshold",
        margeMajore: "and {marge} before the higher one",
        margeMajoreSeule: "You have {marge} left before the higher threshold",
        auSeuilBase: "You are exactly at the basic threshold: one more euro crosses it.",
        margeSuite:
            "The basic threshold would have you charge VAT from 1 January {annee}, the higher threshold from the very day you cross it.",

        bascule1erJanvierTitre: "You will become liable on 1 January {annee}",
        bascule1erJanvierTexte:
            "You have passed the basic threshold by {depassement}, but not the higher one. The exemption therefore runs to 31 December, and you charge VAT from 1 January {annee}, {echeance}.",
        echeanceJours: "in {jours} days",
        echeanceDemain: "tomorrow",
        margeMajoreBascule:
            "You have {marge} left before the higher threshold, which would make you liable the very day you cross it.",
        auSeuilMajore:
            "You are exactly at the higher threshold: one more euro makes you liable that very day.",

        depuisJanvierTitre: "You have been liable since 1 January {annee}",
        depuisJanvierTexte:
            "Last year's turnover, {ca}, passed the basic threshold by {depassement}. Every invoice this year must carry VAT, and you may deduct the VAT on your purchases. If you have invoiced without VAT since January, those invoices must be corrected, deposits included. We did not ask what you are collecting this year: it no longer changes that date.",

        immediatTitre: "You have been liable since the day you passed the threshold",
        immediatTexte:
            "You have passed the higher threshold by {depassement}. VAT is due on transactions carried out from that day, without waiting for 1 January. Invoices issued before it stand as they are; those issued since must be corrected if they do not carry VAT.",

        jaugeTitre: "Your current year against the two thresholds",
        jaugeTitreProjet: "Your first year against the two thresholds",
        jaugeTitreN1: "Last year against the two thresholds",
        seuilBase: "Basic threshold",
        seuilMajore: "Higher threshold",

        rappelTitre: "These thresholds have nothing to do with the micro scheme",
        rappelTexte:
            "Passing a VAT threshold does not take you out of the micro-entreprise, whose ceiling is far higher: {services} for services, {vente} for goods. You simply become liable for VAT, and you remain a micro-entrepreneur.",
        pieges: [
            "Any VAT stated on an invoice is owed by the mere fact of invoicing it, even under the exemption, and your client cannot reclaim it: they lose it.",
            "Under the exemption, no VAT is deductible on your purchases: that is the trade-off.",
            "For lawyers, authors and performing artists, these thresholds apply only to regulated work and transfers of rights. The rest of their activity falls under a separate exemption: {base} basic, {majore} higher.",
            "Care given by regulated health professions, psychologists and osteopaths included, is exempt from VAT and does not count towards the thresholds: only their other services are compared with them.",
            "Private lessons you give yourself, without employees, and that your pupils pay you for directly are exempt from VAT and do not count towards the thresholds.",
        ],
    },
};

/**
 * Franchise des avocats, auteurs et artistes-interprètes pour leurs opérations
 * NON réglementées (reference-2026.json, tva_franchise, « nuance »). Elle
 * n'est pas dans SEUILS_TVA : le parcours ne la calcule pas, il la cite dans
 * le troisième piège. À consolider dans lib/tools/microEnterprise.js.
 */

/**
 * Le curseur monte à 1,6 fois le seuil majoré : de quoi le dépasser franchement.
 * L'année de création, `facteur` le ramène à ses seuils réduits, sans quoi un
 * démarrage en novembre se jouait sur les premiers millimètres de la course.
 */
const PAS_CURSEUR = 500;
function maximumCurseur(categorie, facteur = 1) {
    return Math.max(PAS_CURSEUR * 10, seuilsDe(categorie).majore * facteur * 1.6);
}

/**
 * Les seuils d'une catégorie. Un mixte n'a pas de seuils à lui : il a les DEUX
 * jeux, celui du total et celui des prestations, et c'est tout son problème.
 * Les bornes d'affichage suivent le total.
 */
function seuilsDe(categorie) {
    return SEUILS_TVA[categorie === "mixte" ? "global" : categorie];
}

/** Une position de départ plausible pour le curseur, arrondie au pas. */
function departCurseur(categorie, part) {
    return Math.round((seuilsDe(categorie).base * part) / PAS_CURSEUR) * PAS_CURSEUR;
}

/** Une date ISO en toutes lettres : « 1er juin 2026 », "1 June 2026". */
function dateLongue(iso, lang) {
    const texte = new Intl.DateTimeFormat(lang === "en" ? "en-GB" : "fr-FR", {
        day: "numeric",
        month: "long",
        year: "numeric",
        timeZone: "UTC",
    }).format(new Date(`${iso}T00:00:00Z`));
    return lang === "en" ? texte : texte.replace(/^1 /, "1er ");
}

/**
 * Jauge de l'année en cours, avec ses deux seuils.
 *
 * Les repères sont dessinés APRÈS la barre de remplissage et plus hauts
 * qu'elle : dessinés à l'intérieur, ils disparaissaient sous le remplissage au
 * moment précis où ils comptent, c'est-à-dire une fois le seuil franchi.
 *
 * LA LÉGENDE VIT SOUS LA BARRE, EN FLUX, et non accrochée à chaque repère.
 * Les deux seuils ne sont séparés que de 10 %, 37 500 et 41 250 €, si bien
 * que deux étiquettes centrées sur leur repère se chevauchaient et donnaient à
 * lire « Seuil de basSeuil majoré ». Aucune largeur d'écran ne réglait cela :
 * l'écart entre les deux repères est une proportion, pas des pixels. Les deux
 * traits gardent leur position, et c'est leur épaisseur qui les distingue.
 */
function Jauge({ titre, valeur, base, majore, t, lang }) {
    const maximum = majore * 1.15;
    const pc = (v) => `${Math.min(100, (v / maximum) * 100)}%`;

    const reperes = [
        { seuil: base, nom: t.seuilBase, trait: "w-0.5 bg-gray-400 dark:bg-lightBlue/50" },
        { seuil: majore, nom: t.seuilMajore, trait: "w-1 bg-amber-600 dark:bg-amber-400" },
    ];

    return (
        <div>
            <div className="flex justify-between items-baseline mb-2 gap-3">
                <span className="text-sm font-semibold dark:text-white">{titre}</span>
                <span className="text-sm font-bold tabular-nums dark:text-white shrink-0">
                    {euros(valeur, lang)}
                </span>
            </div>

            <div className="relative h-3 rounded-full bg-gray-100 dark:bg-white/[0.06]">
                <div
                    className={`absolute inset-y-0 left-0 rounded-full transition-all duration-300 ${
                        valeur > majore
                            ? "bg-amber-500"
                            : valeur > base
                              ? "bg-amber-400/70"
                              : "bg-primary"
                    }`}
                    style={{ width: pc(valeur) }}
                />
                {reperes.map(({ seuil, trait }) => (
                    <div
                        key={seuil}
                        className={`absolute h-7 -top-2 -translate-x-1/2 rounded-full ${trait}`}
                        style={{ left: pc(seuil) }}
                    />
                ))}
            </div>

            <div className="flex flex-wrap gap-x-6 gap-y-1.5 mt-4">
                {reperes.map(({ seuil, nom, trait }) => (
                    <div key={seuil} className="flex items-center gap-2">
                        <span className={`h-3.5 rounded-full shrink-0 ${trait}`} />
                        <span className="text-xs text-gray-500 dark:text-lightBlue/70">
                            {nom}{" "}
                            <span className="tabular-nums font-semibold dark:text-lightBlue">
                                {euros(seuil, lang)}
                            </span>
                        </span>
                    </div>
                ))}
            </div>
        </div>
    );
}

/**
 * Le verdict : la date d'effet, la condition qui la déclenche, et les marges.
 *
 * Le compteur vit ici et non dans le composant principal : il démarre au
 * montage, et cette carte n'est montée qu'à l'arrivée du résultat. Les textes
 * reçoivent la progression pour faire monter leurs montants.
 */
function Verdict({ icone, ton, titre, texte, suite, reduit }) {
    const p = useMontants(reduit);
    return (
        <div
            className={`rounded-xl p-5 border ${
                ton === "alerte"
                    ? "border-amber-500/50 bg-amber-500/[0.06]"
                    : ton === "attention"
                      ? "border-gray-200/70 dark:border-white/10"
                      : "border-primary/40 bg-primary/[0.06]"
            }`}
        >
            <div className="flex items-center gap-2.5">
                <SolarIcon
                    name={icone}
                    className={`w-6 h-6 shrink-0 ${
                        ton === "alerte"
                            ? "text-amber-600 dark:text-amber-400"
                            : "text-gray-400 dark:text-lightBlue/60"
                    }`}
                />
                <div className="font-bold dark:text-white leading-snug">{titre}</div>
            </div>
            <p className="text-sm text-gray-600 dark:text-lightBlue mt-3 mb-0">{texte(p)}</p>
            {suite && (
                <p className="text-sm text-gray-600 dark:text-lightBlue mt-2 mb-0">{suite(p)}</p>
            )}
        </div>
    );
}

/** Venu par les soins ou les cours : le verdict ne porte que sur le reste. */
function RappelExoneration({ texte, icone }) {
    return (
        <p className="mt-3 mb-0 text-sm text-gray-600 dark:text-lightBlue flex items-start gap-2">
            <SolarIcon name={icone} className="text-base text-primary shrink-0 mt-0.5" />
            {texte}
        </p>
    );
}

/** Le rappel qui évite le contresens le plus fréquent, et les pièges. */
function Rappel({ t, lang }) {
    return (
        <div className="mt-6 pt-5 border-t border-gray-200/70 dark:border-white/10">
            <div className="text-sm font-semibold dark:text-white">{t.rappelTitre}</div>
            <p className="text-sm text-gray-600 dark:text-lightBlue mt-1.5 mb-0">
                {remplir(t.rappelTexte, {
                    services: euros(PLAFONDS.services, lang),
                    vente: euros(PLAFONDS.vente, lang),
                })}
            </p>
            <ul className="text-xs text-gray-500 dark:text-lightBlue/70 mt-4 space-y-1.5 list-disc pl-4">
                {t.pieges.map((x) => (
                    <li key={x}>
                        {remplir(x, {
                            base: euros(SEUILS_TVA.autresOperations.base, lang),
                            majore: euros(SEUILS_TVA.autresOperations.majore, lang),
                        })}
                    </li>
                ))}
            </ul>
        </div>
    );
}

/**
 * Le départ du parcours quand le dossier du lecteur en connaît le début : les
 * écrans qu'il saute, et les curseurs tels que ces écrans sautés les auraient
 * posés (la situation « établie » et la date d'une création placent chacune
 * les curseurs à l'échelle des seuils). Ce que le dossier donne l'emporte.
 * Dossier vide : les valeurs de départ d'origine, et aucune reprise.
 */
function departDepuisDossier(dossier, maintenant) {
    const { categorie, situation, caN1, caEnCours, debutN1, debutEnCours } = dossier;
    const annee = maintenant.getUTCFullYear();
    const aujourdhuiIso = maintenant.toISOString().slice(0, 10);
    const depart = { caN1: caN1 ?? 0, caEnCours: caEnCours ?? 0, reprise: null };
    if (categorie === undefined) return depart;

    const etapes = [
        { etape: "activite", connu: true },
        { etape: "situation", connu: situation !== undefined },
    ];
    if (situation === undefined) {
        depart.reprise = reprendre(etapes);
        return depart;
    }
    if (situation === "projet") {
        // La date d'un projet n'est pas dans le dossier : le parcours s'y arrête.
        etapes.push({ etape: "debutEnCours", connu: debutEnCours !== undefined });
    } else if (situation === "creee") {
        // Les mêmes bornes que l'écran : une date à venir ne le passe pas.
        const dateConnue =
            debutEnCours !== undefined && debutEnCours >= `${annee}-01-01` && debutEnCours <= aujourdhuiIso;
        etapes.push({ etape: "debutEnCours", connu: dateConnue });
        if (dateConnue) {
            const reduction = seuilsTvaAjustes(categorie, `${debutEnCours}T00:00:00Z`);
            const facteur = reduction?.jours ? reduction.jours / 365 : 1;
            depart.caEnCours = caEnCours ?? departCurseur(categorie, 0.8 * facteur);
            etapes.push({ etape: "caEnCours", connu: caEnCours !== undefined });
        }
    } else {
        depart.caN1 = caN1 ?? departCurseur(categorie, 0.5);
        depart.caEnCours = caEnCours ?? departCurseur(categorie, 0.8);
        // Entière ou commencée en cours d'année : la date de début le dit.
        // Une activité saisonnière ne se lit pas dans le dossier, et le
        // parcours la suppose non saisonnière, comme son propre réglage.
        etapes.push({ etape: "anneeN1", connu: true });
        if (debutN1 !== undefined) etapes.push({ etape: "dateN1", connu: true });
        etapes.push({ etape: "caN1", connu: caN1 !== undefined });
        const depuisJanvier =
            statutTva({
                categorie,
                caN1: depart.caN1,
                caEnCours: depart.caEnCours,
                debutN1: debutN1 ?? null,
                debutEnCours: null,
                saisonnier: false,
            }).statut === "assujetti_depuis_janvier";
        if (!depuisJanvier) etapes.push({ etape: "caEnCours", connu: caEnCours !== undefined });
    }
    etapes.push({ etape: "resultat" });
    depart.reprise = reprendre(etapes);
    return depart;
}

export default function SimulateurSeuilsTva({ lang = "fr", aujourdhui = null }) {
    const t = L[lang] || L.fr;
    const maintenant = useMemo(
        () => (aujourdhui ? new Date(aujourdhui) : new Date("2026-09-06T00:00:00Z")),
        [aujourdhui],
    );

    // Le dossier du lecteur, s'il en a un : les réponses qu'il connaît
    // partent déjà posées, et le parcours s'ouvre sur la première qui manque.
    const { valeurs: dossier } = useDossierOutil();
    const [depart] = useState(() => departDepuisDossier(dossier, maintenant));
    const [categorie, setCategorie] = useState(dossier.categorie ?? null);
    const [situation, setSituation] = useState(dossier.situation ?? null);
    const [caN1, setCaN1] = useState(depart.caN1);
    const [caEnCours, setCaEnCours] = useState(depart.caEnCours);
    // En activité mixte, la part de chaque total qui vient de prestations : le
    // sous-plafond de 37 500 € ne s'apprécie que sur elle.
    const [servicesN1, setServicesN1] = useState(0);
    const [servicesEnCours, setServicesEnCours] = useState(0);
    // L'an dernier était-il une année de création, et faut-il donc l'annualiser ?
    const [debutN1, setDebutN1] = useState(dossier.debutN1 ?? null);
    // Cette année est-elle l'année de création, et depuis quel jour ? Ses seuils
    // se réduisent à proportion des jours d'activité.
    const [debutEnCours, setDebutEnCours] = useState(dossier.debutEnCours ?? null);
    // Saisonnière ou intermittente : l'activité échappe à l'annualisation de
    // l'an dernier comme à la réduction des seuils de l'année de création.
    const [saisonnier, setSaisonnier] = useState(false);
    // Venu par la carte des soins ou des cours : le parcours ne situe que les
    // prestations qui ne sont pas exonérées, et le rappelle au résultat.
    const [horsExoneration, setHorsExoneration] = useState(null);

    const { etape, sens, reduit, aller, retour, recommencer, communs } = useParcours(
        "activite",
        () => {
            setCategorie(null);
            setSituation(null);
            setCaN1(0);
            setCaEnCours(0);
            setServicesN1(0);
            setServicesEnCours(0);
            setDebutN1(null);
            setDebutEnCours(null);
            setSaisonnier(false);
            setHorsExoneration(null);
        },
        PHASES,
        depart.reprise,
    );

    useSignalerDossier({ categorie, situation, caN1, caEnCours }, etape === "resultat");

    const mixte = categorie === "mixte";
    const annee = maintenant.getUTCFullYear();
    const aujourdhuiIso = maintenant.toISOString().slice(0, 10);

    // La date de début de l'année de création, bornée selon la situation : de
    // janvier à aujourd'hui pour qui a démarré, d'aujourd'hui à la fin de l'an
    // prochain pour un projet.
    const creation = situation === "creee" || situation === "projet";
    const bornesDebut =
        situation === "projet"
            ? { min: aujourdhuiIso, max: `${annee + 1}-12-31` }
            : { min: `${annee}-01-01`, max: aujourdhuiIso };
    const debutValide =
        creation &&
        /^\d{4}-\d{2}-\d{2}$/.test(debutEnCours || "") &&
        debutEnCours >= bornesDebut.min &&
        debutEnCours <= bornesDebut.max;
    const debutIso = debutValide ? `${debutEnCours}T00:00:00Z` : null;

    const r = useMemo(() => {
        if (!categorie) return null;
        if (!mixte) {
            return statutTva({ categorie, caN1, caEnCours, debutN1, debutEnCours: debutIso, saisonnier });
        }
        // Les deux conditions à la fois : le total contre 85 000 €, la part
        // prestations contre 37 500 €. Le statut rendu est le plus sévère.
        return statutTvaMixte({
            caVenteN1: Math.max(0, caN1 - servicesN1),
            caServicesN1: Math.min(servicesN1, caN1),
            caVenteEnCours: Math.max(0, caEnCours - servicesEnCours),
            caServicesEnCours: Math.min(servicesEnCours, caEnCours),
            debutN1,
            debutEnCours: debutIso,
            saisonnier,
        });
    }, [categorie, mixte, caN1, caEnCours, servicesN1, servicesEnCours, debutN1, debutIso, saisonnier]);

    // Le curseur de l'année en cours suit ses seuils, réduits ou non.
    const facteurProrata = r?.prorata?.applique ? r.prorata.jours / 365 : 1;

    // La jauge doit montrer le chiffre de la condition qui a cédé, sans quoi
    // elle afficherait un total sous des seuils de prestations.
    const parLesPrestations = mixte && r?.condition === "prestations";
    const chiffreJauge = parLesPrestations ? Math.min(servicesEnCours, caEnCours) : caEnCours;
    const chiffreJaugeN1 = parLesPrestations ? Math.min(servicesN1, caN1) : caN1;

    // L'année dernière décide seule : statutTva teste le seuil de base de l'an
    // dernier avant tout ce que donne l'année en cours, si bien que ce statut
    // ne dépend pas du curseur de cette année. C'est lui qui fait sauter la
    // question de l'année en cours et qui choisit le résultat.
    const depuisJanvier = r?.statut === "assujetti_depuis_janvier";

    const communsEcran = { ...communs, phases: t.phases, retour: t.retour, onRetour: retour };

    // Les deux activités exonérées, qui prennent le même chemin : un écran
    // qui répond, puis le parcours des seuils pour le reste de l'activité.
    const EXONEREES = {
        soins: {
            titre: t.soinsTitre,
            aide: t.soinsAide,
            points: t.soinsPoints,
            question: t.soinsQuestion,
            rappel: t.soinsRappel,
            caAide: t.caAideHorsSoins,
            icone: "stethoscope-linear",
        },
        cours: {
            titre: t.coursTitre,
            aide: t.coursAide,
            points: t.coursPoints,
            question: t.coursQuestion,
            rappel: t.coursRappel,
            caAide: t.caAideHorsCours,
            icone: "square-academic-cap-linear",
        },
    };

    // Un projet peut démarrer l'an prochain : la bascule suit alors son année de
    // démarrage, et non l'année en cours.
    const anneeActivite = situation === "projet" && debutValide ? Number(debutEnCours.slice(0, 4)) : annee;
    const joursAvantJanvier = Math.max(0, joursAvant(`${anneeActivite + 1}-01-01`, maintenant));

    if (etape === "activite") {
        return (
            <Question
                key={etape}
                {...communs}
                phases={t.phases}
                retour={t.retour}
                phase={PHASES.activite}
                titre={t.qActivite}
                aide={remplir(t.qActiviteAide, {
                    services: euros(SEUILS_TVA.services.base, lang),
                    reglementees: euros(SEUILS_TVA.reglementees.base, lang),
                    global: euros(SEUILS_TVA.global.base, lang),
                })}
                choix={[
                    {
                        icone: "sale-linear",
                        titre: t.global,
                        detail: t.globalDetail,
                        cle: "global",
                    },
                    {
                        icone: "pen-2-linear",
                        titre: t.services,
                        detail: t.servicesDetail,
                        cle: "services",
                    },
                    {
                        icone: "medal-ribbon-star-linear",
                        titre: t.reglementees,
                        detail: t.reglementeesDetail,
                        cle: "reglementees",
                    },
                    {
                        icone: "stethoscope-linear",
                        titre: t.soins,
                        detail: t.soinsDetail,
                        cle: "soins",
                    },
                    {
                        icone: "square-academic-cap-linear",
                        titre: t.cours,
                        detail: t.coursDetail,
                        cle: "cours",
                    },
                    {
                        icone: "widget-4-linear",
                        titre: t.mixte,
                        detail: t.mixteDetail,
                        cle: "mixte",
                    },
                ].map(({ cle, ...c }) => ({
                    ...c,
                    onClick: () => {
                        if (EXONEREES[cle]) {
                            aller(cle);
                            return;
                        }
                        setHorsExoneration(null);
                        setCategorie(cle);
                        aller("situation");
                    },
                }))}
            />
        );
    }

    if (EXONEREES[etape]) {
        // Pas de seuil à calculer pour des soins ou des cours exonérés :
        // l'écran répond, dit ce qui reste taxable, et n'ouvre la suite du
        // parcours qu'aux autres prestations.
        const x = EXONEREES[etape];
        return (
            <Ecran
                key={etape}
                {...communsEcran}
                phase={PHASES[etape]}
                titre={x.titre}
                aide={remplir(x.aide, {
                    base: euros(SEUILS_TVA.services.base, lang),
                    majore: euros(SEUILS_TVA.services.majore, lang),
                })}
            >
                <Liste items={x.points} />
                <div className="mt-6 text-sm font-semibold dark:text-white">
                    {x.question}
                </div>
                <Avancer
                    onClick={() => {
                        setHorsExoneration(etape);
                        setCategorie("services");
                        aller("situation");
                    }}
                >
                    {t.exonereContinuer}
                </Avancer>
            </Ecran>
        );
    }

    if (etape === "situation") {
        // Des positions de départ à l'échelle de l'activité : departCurseur
        // place chaque curseur à une fraction du seuil de base de la catégorie
        // choisie, arrondie au pas, 80 % pour l'année en cours et 50 % pour
        // l'an dernier, de sorte qu'on arrive sous les seuils quelle que soit
        // l'activité. Celui qui prépare son projet part de zéro, il n'a rien
        // encaissé.
        return (
            <Question
                key={etape}
                {...communsEcran}
                phase={PHASES.situation}
                titre={t.qSituation}
                aide={t.qSituationAide}
                choix={[
                    {
                        icone: "lightbulb-linear",
                        titre: t.projet,
                        detail: t.projetDetail,
                        onClick: () => {
                            // Changer de situation efface la date : celle d'un
                            // démarrage passé n'a pas de sens pour un projet.
                            if (situation !== "projet") {
                                setDebutEnCours(null);
                                setSaisonnier(false);
                            }
                            setSituation("projet");
                            setCaN1(0);
                            setCaEnCours(0);
                            setDebutN1(null);
                            aller("debutEnCours");
                        },
                    },
                    {
                        icone: "rocket-2-linear",
                        titre: t.creee,
                        detail: t.creeeDetail,
                        onClick: () => {
                            if (situation !== "creee") {
                                setDebutEnCours(null);
                                setSaisonnier(false);
                            }
                            setSituation("creee");
                            setCaN1(0);
                            setServicesN1(0);
                            setDebutN1(null);
                            // Les curseurs se placent après la date, à l'échelle
                            // des seuils de l'année de création.
                            aller("debutEnCours");
                        },
                    },
                    {
                        icone: "history-linear",
                        titre: t.etablie,
                        detail: t.etablieDetail,
                        onClick: () => {
                            setSituation("etablie");
                            setCaN1(departCurseur(categorie, 0.5));
                            setCaEnCours(departCurseur(categorie, 0.8));
                            setServicesN1(departCurseur(categorie, 0.2));
                            setServicesEnCours(departCurseur(categorie, 0.3));
                            setDebutN1(null);
                            setDebutEnCours(null);
                            setSaisonnier(false);
                            aller("anneeN1");
                        },
                    },
                ]}
            />
        );
    }

    if (etape === "debutEnCours") {
        const projet = situation === "projet";
        // Ce que la date fait aux seuils, dit sous le champ avant de continuer.
        const reduction =
            debutValide && !saisonnier ? seuilsTvaAjustes(mixte ? "global" : categorie, debutIso) : null;
        const reductionServices = mixte && reduction?.jours ? seuilsTvaAjustes("services", debutIso) : null;
        const pleins = seuilsDe(categorie);
        const alerte = Boolean(debutEnCours) && !debutValide;

        let effet = t.debutVide;
        if (alerte) {
            effet = projet
                ? remplir(t.debutHorsProjet, { annee: annee + 1 })
                : remplir(t.debutHorsCreee, { annee });
        } else if (debutValide && saisonnier) {
            effet = t.debutSaisonnierEffet;
        } else if (debutValide && !reduction?.jours) {
            effet = t.debutPlein;
        } else if (debutValide && mixte) {
            effet = remplir(t.debutEffetMixte, {
                jours: reduction.jours,
                base: euros(reduction.base, lang),
                majore: euros(reduction.majore, lang),
                baseServices: euros(reductionServices.base, lang),
                majoreServices: euros(reductionServices.majore, lang),
            });
        } else if (debutValide) {
            effet = remplir(t.debutEffet, {
                jours: reduction.jours,
                basePleine: euros(pleins.base, lang),
                majorePleine: euros(pleins.majore, lang),
                base: euros(reduction.base, lang),
                majore: euros(reduction.majore, lang),
            });
        }

        return (
            <Ecran
                key={etape}
                {...communsEcran}
                phase={PHASES.debutEnCours}
                titre={projet ? t.qDebutProjet : t.qDebutCreee}
                aide={t.qDebutAide}
            >
                <ChampDate
                    valeur={debutEnCours || ""}
                    poser={setDebutEnCours}
                    min={bornesDebut.min}
                    max={bornesDebut.max}
                    className="w-full sm:w-72 px-4 py-3 rounded-xl border border-gray-200/70 dark:border-white/10 bg-white dark:bg-white/[0.04] dark:text-white focus:outline-hidden focus:border-primary/60 focus-visible:ring-2 focus-visible:ring-[#D04515]/50 focus-visible:ring-offset-2 dark:focus-visible:ring-offset-bgDark-800 transition-colors duration-200"
                />
                <p
                    className={`text-sm mt-3 mb-0 ${
                        alerte ? "text-amber-700 dark:text-amber-400" : "text-gray-600 dark:text-lightBlue"
                    }`}
                >
                    {effet}
                </p>
                <div className="mt-6">
                    <Segments
                        libelle={t.debutSaisonnier}
                        options={[
                            { cle: "non", libelle: t.saisonnierNon },
                            { cle: "oui", libelle: t.saisonnierOui },
                        ]}
                        valeur={saisonnier ? "oui" : "non"}
                        poser={(cle) => setSaisonnier(cle === "oui")}
                    />
                </div>
                {debutValide && (
                    <Avancer
                        onClick={() => {
                            if (projet) {
                                aller("resultat");
                                return;
                            }
                            // Des positions de départ à l'échelle des seuils de
                            // cette année. Une part prestations plausible, à
                            // ajuster : la laisser à zéro ferait démarrer un
                            // mixte sur une activité qu'il n'a pas.
                            const facteur = reduction?.jours ? reduction.jours / 365 : 1;
                            setCaEnCours(departCurseur(categorie, 0.8 * facteur));
                            setServicesEnCours(departCurseur(categorie, 0.3 * facteur));
                            aller("caEnCours");
                        }}
                    >
                        {t.continuer}
                    </Avancer>
                )}
            </Ecran>
        );
    }

    if (etape === "anneeN1") {
        return (
            <Question
                key={etape}
                {...communsEcran}
                phase={PHASES.anneeN1}
                titre={t.qAnneeN1}
                aide={t.qAnneeN1Aide}
                choix={[
                    {
                        icone: "calendar-linear",
                        titre: t.anneeEntiere,
                        detail: t.anneeEntiereDetail,
                        onClick: () => {
                            setDebutN1(null);
                            setSaisonnier(false);
                            aller("caN1");
                        },
                    },
                    {
                        icone: "rocket-2-linear",
                        titre: t.anneePartielle,
                        detail: t.anneePartielleDetail,
                        onClick: () => {
                            setSaisonnier(false);
                            setDebutN1(`${annee - 1}-01-01`);
                            aller("dateN1");
                        },
                    },
                    {
                        icone: "sun-linear",
                        titre: t.anneeSaisonniere,
                        detail: t.anneeSaisonniereDetail,
                        onClick: () => {
                            // Le BOFiP écarte l'annualisation pour les
                            // saisonniers et les activités intermittentes.
                            setSaisonnier(true);
                            setDebutN1(null);
                            aller("caN1");
                        },
                    },
                ]}
            />
        );
    }

    if (etape === "dateN1") {
        const dateValide =
            /^\d{4}-\d{2}-\d{2}$/.test(debutN1 || "") &&
            Number((debutN1 || "").slice(0, 4)) === annee - 1;
        return (
            <Ecran
                key={etape}
                {...communsEcran}
                phase={PHASES.dateN1}
                titre={t.qDateN1}
                aide={t.qDateN1Aide}
            >
                <ChampDate
                    valeur={debutN1 || ""}
                    poser={setDebutN1}
                    min={`${annee - 1}-01-01`}
                    max={`${annee - 1}-12-31`}
                    className="w-full sm:w-72 px-4 py-3 rounded-xl border border-gray-200/70 dark:border-white/10 bg-white dark:bg-white/[0.04] dark:text-white focus:outline-hidden focus:border-primary/60 focus-visible:ring-2 focus-visible:ring-[#D04515]/50 focus-visible:ring-offset-2 dark:focus-visible:ring-offset-bgDark-800 transition-colors duration-200"
                />
                <p
                    className={`text-sm mt-3 mb-0 ${
                        dateValide
                            ? "text-gray-600 dark:text-lightBlue"
                            : "text-amber-700 dark:text-amber-400"
                    }`}
                >
                    {dateValide
                        ? remplir(t.dateN1Effet, {
                              jours: r?.annualisation?.jours ?? 0,
                              ca: euros(caN1, lang),
                              annualise: euros(caAnnualise(caN1, `${debutN1}T00:00:00Z`), lang),
                          })
                        : remplir(t.dateN1HorsAnnee, { annee: annee - 1 })}
                </p>
                {dateValide && <Avancer onClick={() => aller("caN1")}>{t.continuer}</Avancer>}
            </Ecran>
        );
    }

    if (etape === "caN1") {
        return (
            <Ecran
                key={etape}
                {...communsEcran}
                phase={PHASES.caN1}
                titre={t.qCaN1}
                aide={t.qCaN1Aide}
            >
                <Curseur
                    libelle={t.caN1}
                    aide={horsExoneration ? EXONEREES[horsExoneration].caAide : t.caAide}
                    valeur={caN1}
                    poser={setCaN1}
                    maximum={maximumCurseur(categorie)}
                    pas={PAS_CURSEUR}
                    formater={(v) => euros(v, lang)}
                />
                {mixte && (
                    <div className="mt-6">
                        <Curseur
                            libelle={t.partServices}
                            aide={t.partServicesAide}
                            valeur={Math.min(servicesN1, caN1)}
                            poser={setServicesN1}
                            maximum={caN1}
                            pas={PAS_CURSEUR}
                            formater={(v) => euros(v, lang)}
                        />
                    </div>
                )}
                {/* Redevable depuis janvier : l'année en cours ne change plus
                    rien, on ne la demande pas. */}
                <Avancer onClick={() => aller(depuisJanvier ? "resultat" : "caEnCours")}>
                    {t.continuer}
                </Avancer>
            </Ecran>
        );
    }

    if (etape === "caEnCours") {
        return (
            <Ecran
                key={etape}
                {...communsEcran}
                phase={PHASES.caEnCours}
                titre={situation === "creee" ? t.qCaEnCoursCreee : t.qCaEnCours}
                aide={situation === "creee" && r?.prorata?.applique ? t.qCaEnCoursCreeeAide : t.qCaEnCoursAide}
            >
                <Curseur
                    libelle={t.caEnCours}
                    aide={horsExoneration ? EXONEREES[horsExoneration].caAide : t.caAide}
                    valeur={caEnCours}
                    poser={setCaEnCours}
                    maximum={maximumCurseur(categorie, facteurProrata)}
                    pas={PAS_CURSEUR}
                    formater={(v) => euros(v, lang)}
                />
                {mixte && (
                    <div className="mt-6">
                        <Curseur
                            libelle={t.partServices}
                            aide={t.partServicesAide}
                            valeur={Math.min(servicesEnCours, caEnCours)}
                            poser={setServicesEnCours}
                            maximum={caEnCours}
                            pas={PAS_CURSEUR}
                            formater={(v) => euros(v, lang)}
                        />
                    </div>
                )}
                <Avancer onClick={() => aller("resultat")}>{t.continuer}</Avancer>
            </Ecran>
        );
    }

    const anneeProchaine = String(anneeActivite + 1);

    if (depuisJanvier) {
        return (
            <Resultat onRetour={retour} retour={t.retour} key={etape} titre={t.resultatTitre} phases={t.phases} sens={sens} reduit={reduit}>
                {horsExoneration && (
                    <RappelExoneration
                        texte={EXONEREES[horsExoneration].rappel}
                        icone={EXONEREES[horsExoneration].icone}
                    />
                )}
                <div className="mt-4">
                    <Verdict
                        icone="calendar-linear"
                        ton="alerte"
                        reduit={reduit}
                        titre={t.depuisJanvierTitre.replace("{annee}", String(annee))}
                        texte={(p) =>
                            remplir(t.depuisJanvierTexte, {
                                ca: euros(caN1 * p, lang),
                                depassement: euros(r.depassementBase * p, lang),
                            })
                        }
                    />
                </div>
                {/* La jauge montre l'an dernier, le seul chiffre qui compte ici :
                    c'est lui qui a franchi le seuil de base. */}
                <div className="mt-6 pt-5 border-t border-gray-200/70 dark:border-white/10">
                    <Jauge
                        titre={t.jaugeTitreN1}
                        valeur={chiffreJaugeN1}
                        base={r.base}
                        majore={r.majore}
                        t={t}
                        lang={lang}
                    />
                </div>
                <Rappel t={t} lang={lang} />
                <Recommencer onClick={recommencer} libelle={t.recommencer} />
            </Resultat>
        );
    }

    const echeance =
        joursAvantJanvier === 1
            ? t.echeanceDemain
            : t.echeanceJours.replace("{jours}", String(joursAvantJanvier));

    const verdict = {
        franchise: {
            icone: "check-circle-linear",
            ton: "calme",
            titre: t.franchiseTitre,
            // La mention suit la date du jour : 293 B du CGI jusqu'au
            // 31 décembre 2026, L. 233-3 du CIBS ensuite.
            // Un projet qui démarre plus tard prend la mention de sa date de
            // démarrage, pas celle du jour.
            texte: () =>
                remplir(t.franchiseTexte, {
                    mention: mentionFranchise(
                        situation === "projet" && debutValide && debutEnCours > aujourdhuiIso
                            ? debutIso
                            : maintenant.toISOString(),
                    ),
                }),
            // Posé exactement sur le seuil, « il vous reste 0 € » se lisait
            // comme une contradiction avec « en franchise » : dépasser, c'est
            // être strictement au-dessus, et on le dit.
            suite: (p) => {
                const majore = euros((r.avantMajore ?? 0) * p, lang);
                const marges =
                    r.avantBase === 0
                        ? `${t.auSeuilBase} ${t.margeMajoreSeule.replace("{marge}", majore)}`
                        : `${t.margeBase.replace("{marge}", euros((r.avantBase ?? 0) * p, lang))} ${t.margeMajore.replace("{marge}", majore)}`;
                return `${marges}. ${t.margeSuite.replace("{annee}", anneeProchaine)}`;
            },
        },
        bascule_janvier_prochain: {
            icone: "calendar-linear",
            ton: "attention",
            titre: t.bascule1erJanvierTitre.replace("{annee}", anneeProchaine),
            texte: (p) =>
                remplir(t.bascule1erJanvierTexte, {
                    depassement: euros(r.depassementBase * p, lang),
                    annee: anneeProchaine,
                    echeance,
                }),
            suite: (p) =>
                r.avantMajore === 0
                    ? t.auSeuilMajore
                    : t.margeMajoreBascule.replace(
                          "{marge}",
                          euros((r.avantMajore ?? 0) * p, lang),
                      ),
        },
        assujetti_immediat: {
            icone: "danger-triangle-linear",
            ton: "alerte",
            titre: t.immediatTitre,
            texte: (p) =>
                t.immediatTexte.replace("{depassement}", euros((r.depassement ?? 0) * p, lang)),
        },
    }[r.statut];

    /* Le verdict d'abord : c'est la date que la personne est venue chercher,
       et sur mobile tout ce qui le précède le pousse sous le pli. Le curseur
       et la jauge viennent ensuite, pour explorer. */
    return (
        <Resultat onRetour={retour} retour={t.retour} key={etape} titre={t.resultatTitre} phases={t.phases} sens={sens} reduit={reduit}>
            {horsExoneration && (
                <RappelExoneration
                    texte={EXONEREES[horsExoneration].rappel}
                    icone={EXONEREES[horsExoneration].icone}
                />
            )}

            <div className="mt-4">
                <Verdict {...verdict} reduit={reduit} />
            </div>

            {/* En activité mixte, dire LAQUELLE des deux conditions a cédé :
                c'est la seule information qui permette d'agir dessus. */}
            {mixte && r.statut !== "franchise" && (
                <p className="text-sm text-gray-600 dark:text-lightBlue mt-4 mb-0">
                    {remplir(parLesPrestations ? t.conditionPrestations : t.conditionTotal, {
                        total: euros(caEnCours, lang),
                        presta: euros(Math.min(servicesEnCours, caEnCours), lang),
                        base: euros(r.base, lang),
                    })}
                </p>
            )}

            {/* L'année de création a ses propres seuils : sans cette phrase, la
                jauge montrerait des montants que rien n'explique. */}
            {r.prorata?.applique && (
                <p className="text-sm text-gray-600 dark:text-lightBlue mt-4 mb-0">
                    {remplir(situation === "projet" ? t.prorataTexteProjet : t.prorataTexte, {
                        date: dateLongue(debutEnCours, lang),
                        jours: r.prorata.jours,
                        base: euros(r.base, lang),
                        majore: euros(r.majore, lang),
                        basePleine: euros(r.prorata.seuilsPleins.base, lang),
                        majorePleine: euros(r.prorata.seuilsPleins.majore, lang),
                    })}
                </p>
            )}

            <div className="mt-6 pt-5 border-t border-gray-200/70 dark:border-white/10">
                <div className="text-sm font-semibold dark:text-white mb-3">
                    {situation === "projet" ? t.explorerTitreProjet : t.explorerTitre}
                </div>
                <Curseur
                    libelle={situation === "projet" ? t.caVise : t.caEnCours}
                    aide={t.caExplorer}
                    valeur={caEnCours}
                    poser={setCaEnCours}
                    maximum={maximumCurseur(categorie, facteurProrata)}
                    pas={PAS_CURSEUR}
                    formater={(v) => euros(v, lang)}
                />
                {mixte && (
                    <div className="mt-6">
                        <Curseur
                            libelle={t.partServices}
                            aide={t.partServicesAide}
                            valeur={Math.min(servicesEnCours, caEnCours)}
                            poser={setServicesEnCours}
                            maximum={caEnCours}
                            pas={PAS_CURSEUR}
                            formater={(v) => euros(v, lang)}
                        />
                    </div>
                )}
                <div className="mt-6">
                    <Jauge
                        titre={situation === "projet" ? t.jaugeTitreProjet : t.jaugeTitre}
                        valeur={chiffreJauge}
                        base={r.base}
                        majore={r.majore}
                        t={t}
                        lang={lang}
                    />
                </div>
            </div>

            <Rappel t={t} lang={lang} />
            <Recommencer onClick={recommencer} libelle={t.recommencer} />
        </Resultat>
    );
}
